import { useCallback, useMemo, useState } from "react";

import { Attachment } from "./types";

export const useAttachments = (): {
  list: Attachment[];
  hasAttachments: boolean;
  add: (...newAttachments: Attachment[]) => void;
  remove: (id: string) => void;
  reset: () => void;
} => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  const add = useCallback(
    (...newAttachments: Attachment[]) => {
      setAttachments((prev) => [...prev, ...newAttachments]);
    },
    [setAttachments],
  );

  const remove = useCallback(
    (id: string): void => {
      setAttachments((prev) => {
        const removedFile = prev.find((attachment) => attachment.id === id);
        if (removedFile?.preview) {
          try {
            URL.revokeObjectURL(removedFile.preview);
          } catch (error) {
            console.error("Failed to revoke object URL:", error);
          }
        }
        return prev.filter((attachment) => attachment.id !== id);
      });
    },
    [setAttachments],
  );

  const reset = useCallback((): void => {
    setAttachments((prev) => {
      prev.forEach((attachment) => {
        if (attachment.preview) {
          try {
            URL.revokeObjectURL(attachment.preview);
          } catch (error) {
            console.error("Failed to revoke object URL:", error);
          }
        }
      });
      return [];
    });
  }, [setAttachments]);

  const hasAttachments = useMemo(() => attachments.length > 0, [attachments]);

  return {
    list: attachments,
    hasAttachments,
    add,
    remove,
    reset,
  };
};
